import { api } from '@/lib/api'

import type {
	CreateOutboundNotificationPayload,
	NotificationDetailsDTO,
	NotificationPreferencesDTO,
	NotificationPreferenceUpdate,
	NotificationsDTO,
	NotificationsFilters,
	OutboundNotificationDTO,
	OutboundNotificationsDTO,
	UpdateOutboundNotificationPayload,
} from './types'

/**
 * Fetch the current user's notifications
 * @param {NotificationsFilters} filters - Filters to apply to the notifications
 */
export const fetchNotifications = async (
	filters?: NotificationsFilters,
	signal?: AbortSignal,
) => {
	const baseURL = '/me/notifications/list'

	const searchParams = new URLSearchParams()

	// We only want to fetch dashboard notifications
	searchParams.set('notification_type', 'dashboard')
	searchParams.set('page_size', '100')

	if (filters) {
		Object.entries(filters).forEach(([key, value]) => {
			if (key) {
				searchParams.set(key, value.toString())
			}
		})
	}

	const { data } = await api.get<NotificationsDTO>(
		`${baseURL}?${searchParams.toString()}`,
		{
			signal,
		},
	)

	return data
}

/**
 * Fetch the current user's notification details
 * @param {string} notificationId - The ID of the notification to fetch
 */
export const fetchNotificationDetails = async (
	notificationId: string,
	signal?: AbortSignal,
) => {
	const { data } = await api.get<NotificationDetailsDTO>(
		`/me/notifications/full/${notificationId}?metadata_format=friendly`,
		{
			signal,
		},
	)

	return data
}

/**
 * Mark notifications as read
 * @param {string[]} notificationIds - The IDs of the notifications to mark as read
 */
export const markNotificationsAsRead = async (
	notificationIds: Array<string>,
) => {
	const { data } = await api.post<{ marked_read_successfully: Array<string> }>(
		`/me/notifications/mark_read`,
		{
			notification_ids: notificationIds,
		},
	)

	return data
}

/**
 * Fetch the current user's notification preferences across all tenants.
 *
 * The endpoint is tenant-agnostic (scoped to the authenticated user) and
 * returns preferences grouped by channel. Each channel carries one entry per
 * tenant the user has access to.
 */
export const fetchNotificationPreferences = async (signal?: AbortSignal) => {
	const { data } = await api.get<NotificationPreferencesDTO>(
		'/me/notifications/preferences',
		{
			signal,
		},
	)

	return data
}

/**
 * Update a single notification preference for a (tenant, channel) pair.
 *
 * Omitted `email`/`dashboard` keys are preserved server-side, so callers
 * should only send the field that was toggled.
 *
 * @param {NotificationPreferenceUpdate} payload - Single preference to update
 */
export const updateNotificationPreference = async (
	payload: NotificationPreferenceUpdate,
) => {
	const { data } = await api.post<string>('/me/notifications/preferences', {
		preferences: [payload],
	})

	return data
}

/**
 * List all custom outbound notification recipients for a tenant
 * @param {string} tenant - The tenant ID
 * @param {AbortSignal} signal - AbortSignal for query cancellation
 */
export const listOutboundNotifications = async (
	tenant: string,
	signal?: AbortSignal,
) => {
	const { data } = await api.get<OutboundNotificationsDTO>(
		`/api/tenants/${tenant}/outbound-notifications`,
		{
			signal,
		},
	)

	return data
}

/**
 * Fetch a single outbound notification recipient by ID
 * @param {string} tenant - The tenant ID
 * @param {string} id - The outbound notification UUID
 * @param {AbortSignal} signal - AbortSignal for query cancellation
 */
export const getOutboundNotification = async (
	tenant: string,
	id: string,
	signal?: AbortSignal,
) => {
	const { data } = await api.get<OutboundNotificationDTO>(
		`/api/tenants/${tenant}/outbound-notifications/${id}`,
		{
			signal,
		},
	)

	return data
}

/**
 * Create a new outbound notification recipient for a tenant
 * @param {string} tenant - The tenant ID
 * @param {CreateOutboundNotificationPayload} payload - Recipient email, friendly_name, and channels
 */
export const createOutboundNotification = async (
	tenant: string,
	payload: CreateOutboundNotificationPayload,
) => {
	const { data } = await api.post<OutboundNotificationDTO>(
		`/api/tenants/${tenant}/outbound-notifications`,
		payload,
	)

	return data
}

/**
 * Update an existing outbound notification recipient. Omitted fields are
 * left untouched; `channels` REPLACES the full set when provided.
 * @param {string} tenant - The tenant ID
 * @param {string} id - The outbound notification UUID
 * @param {UpdateOutboundNotificationPayload} payload - Fields to update
 */
export const updateOutboundNotification = async (
	tenant: string,
	id: string,
	payload: UpdateOutboundNotificationPayload,
) => {
	const { data } = await api.patch<OutboundNotificationDTO>(
		`/api/tenants/${tenant}/outbound-notifications/${id}`,
		payload,
	)

	return data
}

/**
 * Delete an outbound notification recipient
 * @param {string} tenant - The tenant ID
 * @param {string} id - The outbound notification UUID
 */
export const deleteOutboundNotification = async (
	tenant: string,
	id: string,
) => {
	await api.delete(`/api/tenants/${tenant}/outbound-notifications/${id}`)
}
